import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Config } from '../../config';
import Sidebar from '../../partials/Sidebar';
import Header from '../../partials/Header';

const getAxiosConfig = () => {
  const token = localStorage.getItem('token');
  return { headers: { access_token: `Bearer ${token}` }, withCredentials: true };
};

function VPNClients() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', client_type: 'tester', description: '', engagement_ids: [] });
  const [creating, setCreating] = useState(false);

  // Engagement picker
  const [showEngagementPicker, setShowEngagementPicker] = useState(null);

  const flash = (msg, isError = false) => {
    setFeedback({ message: msg, isError });
    setTimeout(() => setFeedback(null), 4000);
  };

  const fetchClients = useCallback(async () => {
    try {
      const res = await axios.get(`${Config.APIBaseURL}/vpn/clients`, getAxiosConfig());
      setClients(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load VPN clients');
    }
  }, []);

  const fetchEngagements = useCallback(async () => {
    try {
      const res = await axios.get(`${Config.APIBaseURL}/engagements/`, getAxiosConfig());
      setEngagements(res.data.filter(e => [1, 2, 3, 4, 6].includes(e.subtype_id)));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    Promise.all([fetchClients(), fetchEngagements()]).finally(() => setLoading(false));
  }, [fetchClients, fetchEngagements]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.name.trim()) { flash('Name is required', true); return; }
    setCreating(true);
    try {
      await axios.post(`${Config.APIBaseURL}/vpn/clients`, createForm, getAxiosConfig());
      flash('VPN client created');
      setShowCreate(false);
      setCreateForm({ name: '', client_type: 'tester', description: '', engagement_ids: [] });
      await fetchClients();
    } catch (e) {
      flash(e.response?.data?.detail || 'Failed to create client', true);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`Delete VPN client "${client.name}"? This will revoke its certificate.`)) return;
    try {
      await axios.delete(`${Config.APIBaseURL}/vpn/clients/${client.id}`, getAxiosConfig());
      flash(`Deleted ${client.name}`);
      await fetchClients();
    } catch (e) {
      flash(e.response?.data?.detail || 'Failed to delete', true);
    }
  };

  const handleDownloadConfig = async (client) => {
    try {
      const res = await axios.get(`${Config.APIBaseURL}/vpn/clients/${client.id}/config`, {
        ...getAxiosConfig(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${client.name}.ovpn`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      flash('Failed to download config — certificate may not exist yet', true);
    }
  };

  const handleAddEngagement = async (clientId, engagementId) => {
    try {
      await axios.post(`${Config.APIBaseURL}/vpn/clients/${clientId}/engagements/${engagementId}`, {}, getAxiosConfig());
      flash('Engagement added — routes synced');
      setShowEngagementPicker(null);
      await fetchClients();
    } catch (e) {
      flash(e.response?.data?.detail || 'Failed to add engagement', true);
    }
  };

  const handleRemoveEngagement = async (clientId, engagementId) => {
    try {
      await axios.delete(`${Config.APIBaseURL}/vpn/clients/${clientId}/engagements/${engagementId}`, getAxiosConfig());
      flash('Engagement removed — routes synced');
      await fetchClients();
    } catch (e) {
      flash(e.response?.data?.detail || 'Failed to remove engagement', true);
    }
  };

  const handleSyncAll = async () => {
    try {
      const res = await axios.post(`${Config.APIBaseURL}/vpn/sync-all-routes`, {}, getAxiosConfig());
      flash(`Synced routes for ${res.data.clients} clients`);
    } catch (e) {
      flash('Failed to sync routes', true);
    }
  };

  const getEngagementTitle = (eid) => {
    const eng = engagements.find(e => e.id === eid);
    return eng ? `${eng.code || ''} ${eng.title || ''}`.trim() : `#${eid}`;
  };

  const typeColors = {
    pentagi: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
    tester: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    service: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  };

  const statusColors = {
    active: 'bg-emerald-500/20 text-emerald-400',
    created: 'bg-slate-500/20 text-slate-400',
    revoked: 'bg-red-500/20 text-red-400',
  };

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        <main className="grow">
          <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-6xl mx-auto">

            {/* Header */}
            <div className="sm:flex sm:justify-between sm:items-center mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl text-slate-100 font-bold">VPN Clients</h1>
                <p className="text-sm text-slate-400 mt-1">Manage VPN access for humans and agents</p>
              </div>
              <div className="flex gap-2 mt-4 sm:mt-0">
                <button
                  onClick={handleSyncAll}
                  className="px-3 py-2 text-sm font-medium rounded-lg border border-slate-700 text-slate-300 hover:text-white hover:border-teal-500/50 transition-colors"
                >
                  Sync All Routes
                </button>
                <button
                  onClick={() => setShowCreate(true)}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                >
                  + New Client
                </button>
              </div>
            </div>

            {/* Feedback */}
            {feedback && (
              <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${feedback.isError ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'}`}>
                {feedback.message}
              </div>
            )}
            {error && (
              <div className="mb-4 px-4 py-3 rounded-lg text-sm bg-red-500/10 text-red-400 border border-red-500/20">{error}</div>
            )}

            {/* Create Form */}
            {showCreate && (
              <div className="mb-6 p-6 rounded-xl bg-slate-800/50 border border-slate-700">
                <h3 className="text-lg font-semibold text-slate-100 mb-4">New VPN Client</h3>
                <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Name</label>
                    <input
                      type="text"
                      placeholder="e.g. pentagi, tester-x"
                      className="w-full rounded-lg bg-slate-900 border border-slate-700 p-2.5 text-sm text-slate-200 focus:border-indigo-500 focus:outline-none"
                      value={createForm.name}
                      onChange={(e) => setCreateForm(f => ({ ...f, name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Type</label>
                    <select
                      className="w-full rounded-lg bg-slate-900 border border-slate-700 p-2.5 text-sm text-slate-200 focus:border-indigo-500 focus:outline-none"
                      value={createForm.client_type}
                      onChange={(e) => setCreateForm(f => ({ ...f, client_type: e.target.value }))}
                    >
                      <option value="tester">Tester</option>
                      <option value="pentagi">PentAGI</option>
                      <option value="service">Service</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs text-slate-400 mb-1">Description</label>
                    <input
                      type="text"
                      placeholder="Optional description"
                      className="w-full rounded-lg bg-slate-900 border border-slate-700 p-2.5 text-sm text-slate-200 focus:border-indigo-500 focus:outline-none"
                      value={createForm.description}
                      onChange={(e) => setCreateForm(f => ({ ...f, description: e.target.value }))}
                    />
                  </div>
                  <div className="md:col-span-2 flex gap-2 justify-end">
                    <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">
                      Cancel
                    </button>
                    <button type="submit" disabled={creating} className="px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                      {creating ? 'Creating...' : 'Create Client'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Clients List */}
            {loading ? (
              <div className="text-center text-slate-400 py-12">Loading...</div>
            ) : clients.length === 0 ? (
              <div className="text-center py-16">
                <svg className="w-16 h-16 mx-auto mb-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <p className="text-slate-400 text-lg mb-1">No VPN clients configured</p>
                <p className="text-slate-500 text-sm">Create a client to grant VPN access to PentAGI or testers</p>
              </div>
            ) : (
              <div className="space-y-4">
                {clients.map((client) => (
                  <div key={client.id} className="p-5 rounded-xl bg-slate-800/40 border border-slate-700/50 hover:border-slate-600 transition-colors">
                    {/* Client Header */}
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex items-center gap-3">
                        <h3 className="text-base font-mono font-semibold text-slate-100">{client.name}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${typeColors[client.client_type] || typeColors.service}`}>
                          {client.client_type}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[client.status] || statusColors.created}`}>
                          {client.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDownloadConfig(client)}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg border border-teal-500/30 text-teal-400 hover:bg-teal-500/10 transition-colors"
                          title="Download .ovpn config"
                        >
                          Download .ovpn
                        </button>
                        <button
                          onClick={() => handleDelete(client)}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors"
                        >
                          Revoke
                        </button>
                      </div>
                    </div>

                    {/* Info Row */}
                    <div className="flex items-center gap-6 text-sm text-slate-400 mb-4">
                      <span>VPN IP: <code className="text-teal-400 font-mono">{client.vpn_ip || 'N/A'}</code></span>
                      {client.description && <span className="text-slate-500">— {client.description}</span>}
                      <span className="text-slate-600 text-xs">
                        Created {new Date(client.created_at).toLocaleDateString()}
                      </span>
                    </div>

                    {/* Engagements */}
                    <div className="flex items-center flex-wrap gap-2">
                      <span className="text-xs text-slate-500 mr-1">Engagements:</span>
                      {client.engagement_ids.length === 0 ? (
                        <span className="text-xs text-slate-600 italic">None — no routes will be pushed</span>
                      ) : (
                        client.engagement_ids.map((eid) => (
                          <span key={eid} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-700/50 text-xs text-slate-300">
                            {getEngagementTitle(eid)}
                            <button
                              onClick={() => handleRemoveEngagement(client.id, eid)}
                              className="text-slate-500 hover:text-red-400 transition-colors ml-1"
                              title="Remove engagement"
                            >
                              ×
                            </button>
                          </span>
                        ))
                      )}
                      <div className="relative">
                        <button
                          onClick={() => setShowEngagementPicker(showEngagementPicker === client.id ? null : client.id)}
                          className="px-2 py-1 rounded-lg border border-dashed border-slate-600 text-xs text-slate-500 hover:text-indigo-400 hover:border-indigo-500/30 transition-colors"
                        >
                          + Add
                        </button>
                        {showEngagementPicker === client.id && (
                          <div className="absolute top-full left-0 mt-1 z-20 w-72 max-h-48 overflow-y-auto rounded-lg bg-slate-800 border border-slate-700 shadow-xl">
                            {engagements
                              .filter(e => !client.engagement_ids.includes(e.id))
                              .map(e => (
                                <button
                                  key={e.id}
                                  onClick={() => handleAddEngagement(client.id, e.id)}
                                  className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 transition-colors"
                                >
                                  {e.code || ''} {e.title || `Engagement #${e.id}`}
                                </button>
                              ))}
                            {engagements.filter(e => !client.engagement_ids.includes(e.id)).length === 0 && (
                              <div className="px-3 py-2 text-xs text-slate-500 italic">All engagements assigned</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

          </div>
        </main>
      </div>
    </div>
  );
}

export default VPNClients;
